import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        lpPlatformsCount
        lpPlatformsLastUpdated
      }
    }
  }
`;

const LP_PLATFORM_FIELDS: Record<
  string,
  { apiField: string; isDate?: boolean; isNumber?: boolean }
> = {
  numberOfLpPlatforms: { apiField: 'lpPlatformsCount', isNumber: true },
  numberOfLpPlatformsLastUpdatedOn: {
    apiField: 'lpPlatformsLastUpdated',
    isDate: true
  }
};

export interface EditLpPlatformDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditLpPlatformDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditLpPlatformDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save LP Platform');
      } else {
        toast.success('LP Platform saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving LP Platform:', error);
      toast.error('Failed to save LP Platform');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit LP Platform"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="# of LPPlatforms"
          onChange={v => {
            updateField('numberOfLpPlatforms', v ? parseInt(v) : null);
          }}
          type="number"
          value={value.numberOfLpPlatforms?.toString() ?? ''}
        />
        <EditFormField
          label="# of LPPlatforms (Last Updated On)"
          onChange={v => {
            updateField('numberOfLpPlatformsLastUpdatedOn', v);
          }}
          type="date"
          value={toDateInputValue(value.numberOfLpPlatformsLastUpdatedOn) ?? ''}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, null | number | string> {
  const changes: Record<string, null | number | string> = {};

  for (const [uiField, config] of Object.entries(LP_PLATFORM_FIELDS)) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      if (config.isNumber) {
        changes[config.apiField] = currentVal as null | number;
      } else if (config.isDate) {
        changes[config.apiField] = toDateInputValue(currentVal as string);
      } else {
        const strVal = currentVal as null | string;
        changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
      }
    }
  }

  return changes;
}

function toDateInputValue(dateStr: string): null | string {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}
